import { genericMemo } from "@redotech/react-util/component";
import {
  NumberComparisonExpression,
  NumberComparisonOperator,
  numberComparisonOperators,
  numberComparisonToLabel,
} from "@redotech/redo-model/advanced-flow/expressions/number-expression";
import { Schema } from "@redotech/redo-model/advanced-flow/type-system/schema";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { DeepPartial } from "@redotech/util/type";
import { useEffect, useMemo, useState } from "react";
import { useDelayedAutoFocus } from "../common/use-delayed-auto-focus";

const numberComparisonOperatorItems: RedoListItem<NumberComparisonOperator>[] =
  numberComparisonOperators.map((operator) => ({
    id: operator,
    type: "text",
    text: numberComparisonToLabel[operator],
    value: operator,
  }));

export const NumberInspector = genericMemo(function NumberInspector({
  schema,
  dataType,
  expression,
  setExpression,
  showValidationErrors,
}: {
  schema: Schema;
  dataType: "Number" | "Maybe Number";
  expression: DeepPartial<NumberComparisonExpression>;
  setExpression: (expression: DeepPartial<NumberComparisonExpression>) => void;
  showValidationErrors?: boolean;
}) {
  useEffect(() => {
    if (!expression.type) {
      setExpression({ ...expression, type: "number_comparison" });
    }
    if (!expression.operator) {
      setExpression({ ...expression, operator: "greaterThan" });
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expression]);

  const selectedItem = numberComparisonOperatorItems.find(
    (item) => item.id === expression.operator,
  );

  const [touched, setTouched] = useState(showValidationErrors || false);
  const delayedAutoFocus = useDelayedAutoFocus(
    expression.comparisonValue === undefined,
  );

  const errorText = useMemo(() => {
    if (expression.comparisonValue === undefined && touched) {
      return "A value is required";
    }
    return undefined;
  }, [expression.comparisonValue, touched]);

  return (
    <>
      <RedoSingleSelectDropdownInput<NumberComparisonOperator>
        label="Comparison"
        options={numberComparisonOperatorItems}
        optionSelected={({ item: operator }) => {
          setExpression({ ...expression, operator: operator.value });
        }}
        selectedItem={selectedItem}
        size="xs"
      />

      <RedoTextInput
        autoFocus={delayedAutoFocus}
        description={errorText}
        inputTypeHint="number"
        key={delayedAutoFocus.toString()}
        label="Value"
        onBlur={() => setTouched(true)}
        setValue={(value) => {
          const numValue = value === "" ? undefined : parseFloat(value);
          setExpression({ ...expression, comparisonValue: numValue });
        }}
        state={errorText ? "error" : "default"}
        value={expression.comparisonValue?.toString() ?? ""}
      />
    </>
  );
});
